"use client"

import type { AttackType, DefenseType } from "@/types/hero.types"
import { getDamageMultiplier, getEffectivenessColor } from "@/systems/damage-system"

interface DamageTypeTooltipProps {
  damageType: AttackType
  fullWidth?: boolean
}

// All defense types for the table
const defenseTypes: DefenseType[] = [
  "unarmored",
  "light",
  "medium",
  "heavy",
  "fortified",
  "hero",
  "divine",
]

// Descriptions for each damage type
const damageTypeDescriptions: Record<AttackType, string> = {
  normal: "Standard physical attacks. Strong vs medium armor.",
  pierce: "Armor-penetrating. Excellent vs light, weak vs heavy.",
  siege: "Anti-fortification. Strong vs fortified structures.",
  hero: "Balanced damage. Consistent against most targets.",
  magic: "Elemental damage. Strong vs heavy, weak vs fortified.",
  spells: "Spell damage. Slightly reduced vs fortified.",
  chaos: "Pure damage. Ignores all armor type resistances.",
}

// Colors for damage type names
const damageTypeColors: Record<AttackType, string> = {
  normal: "#FFFFFF",
  pierce: "#FFD700",
  siege: "#8B4513",
  hero: "#FF6B6B",
  chaos: "#9932CC",
  magic: "#00BFFF",
  spells: "#FF69B4",
}

const formatTypeName = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

export const DamageTypeTooltip = ({ damageType, fullWidth = false }: DamageTypeTooltipProps) => {
  return (
    <div className={fullWidth ? "w-full p-3" : "w-40 p-2"}>
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-white/20">
        <span
          className={fullWidth ? "font-bold text-sm" : "font-bold text-xs"}
          style={{ color: damageTypeColors[damageType] }}
        >
          {formatTypeName(damageType)}
        </span>
        <span className={fullWidth ? "text-white/70 text-sm" : "text-white/70 text-xs"}>Damage</span>
      </div>

      {/* Effectiveness table */}
      <div className={fullWidth ? "space-y-1 text-sm" : "space-y-0.5 text-[10px]"}>
        {defenseTypes.map((defType) => {
          const multiplier = getDamageMultiplier(damageType, defType)
          const percent = Math.round(multiplier * 100)
          const color = getEffectivenessColor(multiplier)

          return (
            <div key={defType} className="flex justify-between items-center">
              <span className="text-white/60">vs {formatTypeName(defType)}</span>
              <span className="font-semibold" style={{ color }}>
                {percent}%
              </span>
            </div>
          )
        })}
      </div>

      {/* Description */}
      <div className="mt-2 pt-1.5 border-t border-white/20">
        <p className={fullWidth ? "text-xs text-white/50 leading-tight" : "text-[9px] text-white/50 leading-tight"}>
          {damageTypeDescriptions[damageType]}
        </p>
      </div>
    </div>
  )
}
